import React, { useRef, useState, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import EventHoverCard from './EventHoverCard';
import './calendar-overrides.css';

export default function CalendarView({
  events,
  loading,
  onDateSelect,
  onEventClick,
  onEventDrop,
  onEventResize,
  height = 'auto',
}) {
  const calendarRef = useRef(null);
  const [hoverEvent, setHoverEvent] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  const handleDateSelect = (info) => {
    onDateSelect?.({
      start: info.start,
      end: info.end,
      allDay: info.allDay,
    });
    info.view?.calendar?.unselect?.();
  };

  const handleEventClick = (info) => {
    info.jsEvent?.preventDefault?.();
    setHoverEvent(null);
    onEventClick?.(info.event);
  };

  const handleEventDrop = async (info) => {
    const props = info.event.extendedProps;
    if (!props.isEditable || props.sourceTable !== 'scheduled_events') {
      info.revert();
      return;
    }
    const ok = await onEventDrop?.({
      id: props.sourceId,
      startAt: info.event.start?.toISOString(),
      endAt: info.event.end?.toISOString() || null,
    });
    if (!ok) info.revert();
  };

  const handleEventResize = async (info) => {
    const props = info.event.extendedProps;
    if (!props.isEditable || props.sourceTable !== 'scheduled_events') {
      info.revert();
      return;
    }
    const ok = await onEventResize?.({
      id: props.sourceId,
      startAt: info.event.start?.toISOString(),
      endAt: info.event.end?.toISOString() || null,
    });
    if (!ok) info.revert();
  };

  const eventDidMount = (info) => {
    if (info.event.extendedProps?.isEditable === false) {
      info.el.setAttribute('data-noneditable', 'true');
    }
  };

  const handleEventMouseEnter = useCallback((info) => {
    const rect = info.el.getBoundingClientRect();
    setHoverPosition({ x: rect.right + 8, y: rect.top });
    setHoverEvent(info.event);
  }, []);

  const handleEventMouseLeave = useCallback(() => {
    setHoverEvent(null);
  }, []);

  const renderEventContent = (eventInfo) => {
    const props = eventInfo.event.extendedProps || {};
    const teamAttendees = Array.isArray(props.teamAttendees) ? props.teamAttendees : [];
    const time = eventInfo.timeText;
    const title = eventInfo.event.title;
    const viewType = eventInfo.view?.type || '';
    const isListView = viewType.startsWith('list');

    if (isListView) return undefined;

    return (
      <div className="flex items-center gap-1 overflow-hidden w-full px-0.5 py-px">
        {teamAttendees.length > 0 && (
          <div className="flex items-center flex-shrink-0">
            {teamAttendees.map((member, i) => (
              <span
                key={`${member.initial}-${i}`}
                className="flex-shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] font-bold leading-none -ml-1 first:ml-0 ring-1 ring-white"
                style={{
                  backgroundColor: member.bg,
                  color: member.text,
                  zIndex: teamAttendees.length - i,
                }}
                title={member.name}
                aria-label={member.name}
              >
                {member.initial}
              </span>
            ))}
          </div>
        )}
        <div className="truncate min-w-0 text-xs leading-tight">
          {time && <span className="font-semibold mr-1">{time}</span>}
          <span>{title}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="relative h-full flex flex-col">
      {loading && (
        <div className="absolute top-2 right-2 z-20 text-xs text-slate-500 bg-white/90 border border-slate-200 rounded px-2 py-1 shadow-sm">
          Carregando...
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex-1 min-h-0">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView="dayGridMonth"
          locale={ptBrLocale}
          timeZone="local"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
          }}
          buttonText={{
            today: 'hoje',
            month: 'mês',
            week: 'semana',
            day: 'dia',
            list: 'lista',
          }}
          events={events}
          editable={true}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={4}
          moreLinkClick="day"
          select={handleDateSelect}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventDidMount={eventDidMount}
          eventContent={renderEventContent}
          eventMouseEnter={handleEventMouseEnter}
          eventMouseLeave={handleEventMouseLeave}
          nowIndicator={true}
          height={height}
          stickyHeaderDates={true}
        />
      </div>
      {hoverEvent && <EventHoverCard event={hoverEvent} position={hoverPosition} />}
    </div>
  );
}
